import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { FullPageLoader } from '@/components/ui/full-page-loader';

interface FeatureAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
}

interface FeatureGateProps {
  children: ReactNode;
  /** Hook that returns { hasAccess, isLoading } */
  useAccess: () => FeatureAccessResult;
  /** Path to redirect to if access is denied */
  fallbackPath: string;
}

/**
 * Generic route protection component for feature-gated pages. (TD-003)
 * Waits for access check to complete before making redirect decision.
 * 
 * @example
 * <FeatureGate useAccess={useVocabularyListsAccess} fallbackPath="/vocabulary">
 *   <Lists />
 * </FeatureGate>
 */
export function FeatureGate({ children, useAccess, fallbackPath }: FeatureGateProps) {
  const { hasAccess, isLoading } = useAccess();

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
